import { Switch, notification } from 'antd';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { DocumentController } from '@/components/Shared/ui';
import { downloadFilesApi } from '@/integrations/index.api';
import { handleError } from '@/services/handleError';
import { useAppSelector } from '@/store';
import type { TFileAttached } from '@/types/financing';
import type { FileType } from '@/types/uploadFiles';

import { type FormType, fileDict } from '../../features/euras-bank-v2';

interface IProps {
  files: Record<string, TFileAttached> | undefined;
  filesOnModeration: Record<string, TFileAttached> | undefined;
  onSubmit: (values: Record<string, FileType>) => Promise<boolean>;
}

const EurasInitialPayment: React.FC<IProps> = ({
  files,
  filesOnModeration,
  onSubmit
}) => {
  const {
    userAccesses: {
      global: { isAdmin },
      autoCredit: { canConfirmInitialPayment }
    }
  } = useAppSelector((state) => state.userInfo);
  const { selectedBank } = useAppSelector((state) => state.financingStep);

  const { control } = useFormContext<FormType>();

  const [clientConfirmedPayment, setClientConfirmedPayment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isInitialPaymentConfirmation =
    selectedBank?.status.code === 'mobile_client';
  const isOnRework = selectedBank?.status.code === 'on_rework';
  const shouldShowResendButton =
    isAdmin && selectedBank?.status.code === 'mobile_client';

  const resendInitialPayment = async () => {
    if (!files?.initialPaymentConfirmation) return;

    try {
      setIsSubmitting(true);
      const document = await downloadFilesApi.downloadBankFile(
        files.initialPaymentConfirmation.uuid
      );
      const response = await onSubmit({
        initial_payment_confirmation: {
          size: 0,
          name: files.initialPaymentConfirmation.name,
          base64: document.base64
        }
      });
      if (response) {
        notification.success({ title: 'Документ успешно отправлен' });
      }
    } catch (error) {
      handleError(error, {
        customTitle: 'Произошла ошибка. Пожалуйста, попробуйте позже'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return files?.initialPaymentConfirmation
    ? !isOnRework && (
        <>
          <label className="euras-bank-v2__switch">
            <Switch checked disabled={!canConfirmInitialPayment} />
            Клиент подтвердил оплату ПВ через кассу
          </label>
          <DocumentController
            title={fileDict.initialPaymentConfirmation}
            fileLink={files.initialPaymentConfirmation.uuid}
            downloadable
            resend={
              shouldShowResendButton
                ? {
                    resendFile: resendInitialPayment,
                    isResendLoading: isSubmitting
                  }
                : undefined
            }
          />
        </>
      )
    : (isInitialPaymentConfirmation ||
        filesOnModeration?.initialPaymentConfirmation) && (
        <>
          <label className="euras-bank-v2__switch">
            <Switch
              onChange={setClientConfirmedPayment}
              checked={clientConfirmedPayment}
              disabled={!canConfirmInitialPayment}
            />
            Клиент подтвердил оплату ПВ через кассу
          </label>
          <DocumentController
            control={control}
            isFrozen={!clientConfirmedPayment}
            isRequired
            name="initialPaymentConfirmation"
            title={fileDict.initialPaymentConfirmation}
            uploadable
          />
        </>
      );
};

export default EurasInitialPayment;
